import React, { useState, useEffect, lazy, Suspense } from 'react';
import { ArrowRight, Trash2, Star, StarOff } from 'lucide-react';
import { UnitCategory } from '../types/units';
import { convertValue } from '../utils/conversions';
import { unitsByCategory } from '../utils/units';
import { getConversionSteps, getConversionFormula } from '../utils/conversions';
import { IoSwapHorizontal } from 'react-icons/io5';

interface ConversionFormProps {
  category: string;
  defaultFromUnit?: string;
  defaultToUnit?: string;
}

// Lazy load chart component
const Chart = lazy(() => import('../components/Chart'));

export const ConversionForm: React.FC<ConversionFormProps> = ({ category, defaultFromUnit = '', defaultToUnit = '' }) => {
  const [value, setValue] = useState<string>('');
  const [fromUnit, setFromUnit] = useState<string>(defaultFromUnit);
  const [toUnit, setToUnit] = useState<string>(defaultToUnit);
  const [result, setResult] = useState<number | null>(null);
  const [precision, setPrecision] = useState<number>(4);
  const [] = useState(false);
  const [recentConversions, setRecentConversions] = useState<Array<{
    value: string;
    fromUnit: string;
    toUnit: string;
    category: string;
    timestamp: Date;
  }>>([]);
  const [favorites, setFavorites] = useState<Array<{
    fromUnit: string;
    toUnit: string;
    category: string;
    label: string;
  }>>([]);
  const [showSteps, setShowSteps] = useState(true);
  const [showFormulas, setShowFormulas] = useState(false);
  const [showChart, setShowChart] = useState(false);

  // Update units when category or defaults change
  useEffect(() => {
    if (defaultFromUnit) {
      setFromUnit(defaultFromUnit);
    } else {
      setFromUnit(unitsByCategory[category]?.[0]?.value || '');
    }
    
    if (defaultToUnit) {
      setToUnit(defaultToUnit);
    } else {
      setToUnit(unitsByCategory[category]?.[1]?.value || '');
    }
  }, [category, defaultFromUnit, defaultToUnit]);

  // Load recent conversions from localStorage
  useEffect(() => {
    const savedRecent = localStorage.getItem('recentConversions');
    if (savedRecent) {
      setRecentConversions(JSON.parse(savedRecent));
    }
  }, []);

  // Save recent conversions to localStorage
  useEffect(() => {
    if (recentConversions.length > 0) {
      localStorage.setItem('recentConversions', JSON.stringify(recentConversions));
    }
  }, [recentConversions]);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('favoriteConversions');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage
  useEffect(() => {
    if (favorites.length > 0) {
      localStorage.setItem('favoriteConversions', JSON.stringify(favorites));
    }
  }, [favorites]);

  const handleConvert = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value || !fromUnit || !toUnit) return;

    const converted = convertValue(Number(value), fromUnit, toUnit, category);
    setResult(converted);

    // Add to recent conversions
    setRecentConversions(prev => [{
      value,
      fromUnit,
      toUnit,
      category,
      timestamp: new Date()
    }, ...prev.slice(0, 9)]);
  };

  const clearRecentConversions = () => {
    setRecentConversions([]);
    localStorage.removeItem('recentConversions');
  };

  const toggleFavorite = () => {
    if (!fromUnit || !toUnit) return;
    
    const isFavorite = favorites.some(
      f => f.fromUnit === fromUnit && f.toUnit === toUnit && f.category === category
    );

    if (isFavorite) {
      setFavorites(prev => prev.filter(
        f => !(f.fromUnit === fromUnit && f.toUnit === toUnit && f.category === category)
      ));
    } else {
      const label = `${fromUnit} to ${toUnit}`;
      setFavorites(prev => [...prev, { fromUnit, toUnit, category, label }]);
    }
  };

  // Generate chart data
  const generateChartData = () => {
    if (!fromUnit || !toUnit) return [];
    const points = 10;
    const step = Number(value) / points || 1;
    return Array.from({ length: points + 1 }, (_, i) => {
      const inputValue = step * i;
      return {
        input: inputValue,
        output: convertValue(inputValue, fromUnit, toUnit, category)
      };
    });
  };

  // Function to swap units
  const handleSwapUnits = () => {
    const tempFromUnit = fromUnit;
    setFromUnit(toUnit);
    setToUnit(tempFromUnit);
    // Recalculate with swapped units if there's a value
    if (value) {
      const newResult = convertValue(Number(value), toUnit, tempFromUnit, category);
      setResult(newResult);
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg p-6 mt-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Basic Conversion</h2>
        <p className="text-sm text-gray-600">
          Convert between any units in this category with instant results. Your recent conversions and favorites are saved automatically for quick access.
        </p>
      </div>

      <form onSubmit={handleConvert} className="space-y-6">
        {/* Value Input */}
        <input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter value"
          className="w-full px-4 py-2 border border-gray-200 rounded-lg"
          step="any"
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* From Unit Select */}
          <select
            value={fromUnit}
            onChange={(e) => setFromUnit(e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            required
          >
            <option value="">From</option>
            {unitsByCategory[category]?.map(unit => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>

          {/* Swap Button */}
          <button
            type="button"
            onClick={handleSwapUnits}
            className="self-center p-2 text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Swap units"
          >
            <IoSwapHorizontal className="w-6 h-6" />
          </button>

          {/* To Unit Select */}
          <select
            value={toUnit}
            onChange={(e) => setToUnit(e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            required
          >
            <option value="">To</option>
            {unitsByCategory[category]?.map(unit => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>
        </div>

        {/* Options Row */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <label>Precision:</label>
            <select
              value={precision}
              onChange={(e) => setPrecision(Number(e.target.value))}
              className="px-2 py-1 border border-gray-200 rounded"
            >
              {Array.from({ length: 16 }, (_, i) => (
                <option key={i} value={i}>
                  {i} {i === 1 ? 'decimal' : 'decimals'}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showSteps}
              onChange={(e) => setShowSteps(e.target.checked)}
              className="rounded text-blue-600"
            />
            Show steps
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showFormulas}
              onChange={(e) => setShowFormulas(e.target.checked)}
              className="rounded text-blue-600"
            />
            Show formulas
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showChart}
              onChange={(e) => setShowChart(e.target.checked)}
              className="rounded text-blue-600"
            />
            Show chart
          </label>
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all font-medium"
          >
            Convert
          </button>
          <button
            type="button"
            onClick={toggleFavorite}
            className="px-3 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={favorites.some(f => f.fromUnit === fromUnit && f.toUnit === toUnit) 
              ? "Remove from favorites" 
              : "Add to favorites"}
          >
            {favorites.some(f => f.fromUnit === fromUnit && f.toUnit === toUnit && f.category === category) ? (
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            ) : (
              <StarOff className="w-5 h-5" />
            )}
          </button>
        </div>
      </form>

      {result !== null && (
        <>
          <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm text-gray-600">From</p>
                <p className="font-medium text-gray-900">
                  {Number(value).toLocaleString(undefined, {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision
                  })} {fromUnit}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">To</p>
                <p className="font-medium text-gray-900">
                  {result.toLocaleString(undefined, {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision
                  })} {toUnit}
                </p>
              </div>
            </div>
          </div>

          {/* Conversion Steps */}
          {showSteps && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Step-by-step Conversion</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                {getConversionSteps(Number(value), fromUnit, toUnit, category).map((step, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="font-medium text-gray-700">{index + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Conversion Formula */}
          {showFormulas && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Conversion Formula</h3>
              <div className="text-sm text-gray-600 font-mono bg-white p-2 rounded border border-gray-200">
                {getConversionFormula(fromUnit, toUnit, category)}
              </div>
            </div>
          )}

          {/* Conversion Chart */}
          {showChart && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Conversion Chart</h3>
              <div className="w-full h-[200px]">
                <Suspense fallback={<div>Loading chart...</div>}>
                  <Chart 
                    data={generateChartData()}
                    precision={precision}
                  />
                </Suspense>
              </div>
            </div>
          )}
        </>
      )}

      {favorites.length > 0 && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-700">Favorite Conversions</h3>
            <button
              onClick={() => {
                setFavorites([]);
                localStorage.removeItem('favoriteConversions');
              }}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Clear favorites"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {favorites
              .filter(f => f.category === category)
              .map((fav, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setFromUnit(fav.fromUnit);
                    setToUnit(fav.toUnit);
                  }}
                  className="w-full p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm flex justify-between items-center transition-colors"
                >
                  <span>{fav.fromUnit} → {fav.toUnit}</span>
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                </button>
              ))}
          </div>
        </div>
      )}

      {recentConversions.length > 0 && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-700">Recent Conversions</h3>
            <button
              onClick={clearRecentConversions}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Clear recent conversions"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {recentConversions.slice(0, 5).map((conv, index) => (
              <div
                key={index}
                className="p-2 bg-gray-50 rounded-lg text-sm flex justify-between items-center"
              >
                <span>
                  {Number(conv.value).toLocaleString(undefined, {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision
                  })} {conv.fromUnit} → {conv.toUnit}
                </span>
                <span className="text-gray-400 text-xs">
                  {new Date(conv.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};